import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Partials/Sidebar";
import TrialBanner from "../Partials/TrialBanner";
import {
  fetchContractsPaginated,
  resendReminder,
  deleteEnvelope,
} from "../../api/envelope";
import type { ContractSummary } from "../../api/envelope";
import "../../Styles/DashboardPageStyles/Dashboard.css";
import "../../Styles/Pages/DocumentStatus.css";
import EmptyState from "../Partials/EmptyState";
import { SentIllustration } from "../Partials/EmptyStateIllustrations";

function DocumentStatusPage() {
  const navigate = useNavigate();

  // Data state
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");

  // Reminder feedback
  const [remindingURL, setRemindingURL] = useState<string | null>(null);

  // Delete feedback
  const [deletingURL, setDeletingURL] = useState<string | null>(null);

  // Debounce timer ref for search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSearchRef = useRef(search);

  const fetchIdRef = useRef(0);
  const fetchData = useCallback(
    async (currentPage: number, currentSearch: string, currentStatus: string, currentSort: string) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        setError(null);
        const res = await fetchContractsPaginated({
          page: currentPage,
          limit: 10,
          search: currentSearch,
          status: currentStatus,
          sort: currentSort,
        });
        if (id !== fetchIdRef.current) return;
        setContracts(res.contracts);
        setTotal(res.total);
        setPage(res.page);
        setTotalPages(res.totalPages);
      } catch (err: any) {
        if (id !== fetchIdRef.current) return;
        console.error("FetchContractsFailed", err);
        setError("Could not load documents. Please try again.");
      } finally {
        if (id === fetchIdRef.current) setLoading(false);
      }
    },
    []
  );

  // Fetch when status, sort, or page change (immediate)
  useEffect(() => {
    fetchData(page, search, status, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, sort]);

  // Debounced fetch on search change (skip if value hasn't actually changed)
  useEffect(() => {
    if (prevSearchRef.current === search) return;
    prevSearchRef.current = search;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchData(1, search, status, sort);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleRemind = async (contractURL: string) => {
    try {
      setRemindingURL(contractURL);
      await resendReminder(contractURL);
      toast.success("Reminder sent successfully.");
    } catch (err: any) {
      console.error("RemindFailed", err);
      toast.error("Could not send reminder. Please try again.");
    } finally {
      setRemindingURL(null);
    }
  };

  const handleDelete = async (contractURL: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this document? This action cannot be undone."
    );
    if (!confirmed) return;
    try {
      setDeletingURL(contractURL);
      await deleteEnvelope(contractURL);
      toast.success("Document deleted");
      // Refresh the list
      fetchData(page, search, status, sort);
    } catch (err: any) {
      console.error("DeleteFailed", err);
      toast.error("Could not delete document. Please try again.");
    } finally {
      setDeletingURL(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusPillClass = (computedStatus: string) => {
    const s = computedStatus.toLowerCase().replace(/[\s_]+/g, "-");
    switch (s) {
      case "sent":
        return "status-pill--sent";
      case "partially-signed":
        return "status-pill--partially-signed";
      case "completed":
        return "status-pill--completed";
      case "voided":
        return "status-pill--voided";
      case "expired":
        return "status-pill--expired";
      default:
        return "status-pill--sent";
    }
  };

  const getRecipientSummary = (recipients: ContractSummary["recipients"]) => {
    if (!recipients || recipients.length === 0) return "0/0 signed";
    const signedCount = recipients.filter((r) => r.signed).length;
    return `${signedCount}/${recipients.length} signed`;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }

    return (
      <nav className="ds-pagination" aria-label="Document pagination">
        <button
          className="ds-pagination-btn"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          aria-label="Go to previous page"
        >
          Previous
        </button>

        <div className="ds-pagination-pages">
          {pages.map((p, idx) =>
            typeof p === "number" ? (
              <button
                key={idx}
                className={`ds-pagination-page ${p === page ? "is-active" : ""}`}
                onClick={() => setPage(p)}
                aria-label={`Go to page ${p}`}
                aria-current={p === page ? "page" : undefined}
              >
                {p}
              </button>
            ) : (
              <span key={idx} className="ds-pagination-ellipsis" aria-hidden="true">
                ...
              </span>
            )
          )}
        </div>

        <button
          className="ds-pagination-btn"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          aria-label="Go to next page"
        >
          Next
        </button>
      </nav>
    );
  };

  return (
    <div className="dashboard">
      <Sidebar active="Document Status" />

      <div className="dashboard-main">
        <TrialBanner />
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-header-left">
            <h1 className="dashboard-title">Document Status</h1>
            <p className="dashboard-subtitle">
              Track the status of your agreements in progress
            </p>
          </div>
          <button
            className="primary-button"
            onClick={() => navigate("/send")}
          >
            Send document for signature
          </button>
        </div>

        {/* Filter bar */}
        <div className="ds-filter-container">
          <div className="document-status-controls">
            <input
              className="document-search"
              type="text"
              placeholder="Search by document name or recipient"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search documents"
            />
            <div className="ds-filter-group">
              <select
                className="document-filter"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="partially_signed">Partially Signed</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="voided">Voided</option>
              </select>
              <select
                className="document-sort"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                aria-label="Sort documents"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="document-status-card">
          {loading && (
            <div className="ds-loading" role="status" aria-live="polite">
              <div className="ds-loading-spinner" aria-hidden="true" />
              <p>Loading documents...</p>
            </div>
          )}

          {!loading && contracts.length === 0 && (
            search || status ? (
              <div className="ds-empty-state">
                <p className="ds-empty-title">No documents found</p>
                <p className="ds-empty-subtitle">
                  Try adjusting your search or filters.
                </p>
              </div>
            ) : (
              <EmptyState
                icon={<SentIllustration />}
                title="No documents sent yet"
                subtitle="Documents you send for signature will appear here with real-time status tracking"
                actionLabel="Send Your First Document"
                onAction={() => navigate("/send")}
                preview={
                  <div
                    style={{
                      background: "#ffffff",
                      borderRadius: 14,
                      border: "1px solid #e5e7eb",
                      overflow: "hidden",
                    }}
                  >
                    {/* Ghost header row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 80px",
                        gap: 12,
                        padding: "10px 14px",
                        background: "#f9fafb",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ height: 10, borderRadius: 5, background: "#e2e8f0" }} />
                      <div style={{ height: 10, borderRadius: 5, background: "#e2e8f0" }} />
                      <div style={{ height: 10, borderRadius: 5, background: "#e2e8f0" }} />
                      <div style={{ height: 10, borderRadius: 5, background: "#e2e8f0" }} />
                      <div style={{ height: 10, borderRadius: 5, background: "#e2e8f0" }} />
                    </div>
                    {/* Ghost data rows */}
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr 1fr 1fr 80px",
                          gap: 12,
                          padding: "12px 14px",
                          borderBottom: i < 3 ? "1px solid #f1f5f9" : "none",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ height: 10, borderRadius: 5, background: "#f1f5f9", width: `${60 + i * 10}%` }} />
                        <div style={{ height: 10, borderRadius: 5, background: "#f1f5f9" }} />
                        <div style={{ height: 20, borderRadius: 999, background: i === 1 ? "#eff6ff" : i === 2 ? "#fef3c7" : "#ecfdf3", width: 60 }} />
                        <div style={{ height: 10, borderRadius: 5, background: "#f1f5f9" }} />
                        <div style={{ height: 10, borderRadius: 5, background: "#f1f5f9", width: "60%" }} />
                      </div>
                    ))}
                  </div>
                }
              />
            )
          )}

          {!loading && !error && contracts.length > 0 && (
            <>
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Date Sent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.contractURL}>
                      <td className="ds-doc-name">{c.contractName}</td>
                      <td>{getRecipientSummary(c.recipients)}</td>
                      <td>
                        <span
                          className={`status-pill ${getStatusPillClass(c.computedStatus)}`}
                        >
                          {c.computedStatus}
                        </span>
                      </td>
                      <td>{formatDate(c.dateSent)}</td>
                      <td>
                        <div className="action-cell">
                          <button
                            className="link-button"
                            onClick={() =>
                              navigate(`/document-status/${c.contractURL}`)
                            }
                            aria-label={`View details for ${c.contractName}`}
                          >
                            View
                          </button>
                          {c.computedStatus.toLowerCase() !== "completed" &&
                            c.computedStatus.toLowerCase() !== "voided" && (
                              <button
                                className="link-button"
                                disabled={remindingURL === c.contractURL}
                                onClick={() => handleRemind(c.contractURL)}
                                aria-label={`Send reminder for ${c.contractName}`}
                              >
                                {remindingURL === c.contractURL
                                  ? "Sending..."
                                  : "Remind"}
                              </button>
                            )}
                          {c.computedStatus.toLowerCase() === "completed" && (
                            <a
                              className="link-button"
                              href={`${process.env.REACT_APP_API_BASE || ""}/api/envelope/${c.contractURL}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Download ${c.contractName}`}
                            >
                              Download
                            </a>
                          )}
                          {(c.computedStatus.toLowerCase() === "completed" ||
                            c.computedStatus.toLowerCase() === "voided") && (
                            <button
                              className="link-button"
                              style={{ color: "#dc2626" }}
                              disabled={deletingURL === c.contractURL}
                              onClick={() => handleDelete(c.contractURL)}
                              aria-label={`Delete ${c.contractName}`}
                            >
                              {deletingURL === c.contractURL
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="ds-pagination-bar">
                <span className="ds-pagination-info">
                  Showing {Math.min((page - 1) * 10 + 1, total)}&ndash;{Math.min(page * 10, total)} of {total} documents
                  <span className="ds-pagination-page-info"> &middot; Page {page} of {totalPages}</span>
                </span>
                {renderPagination()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentStatusPage;
